import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
  PAYMENT_PROOF_FIELD_KEYS,
  DEFAULT_PAYMENT_PROOF_PRIMARY_COLOR,
  normalizePaymentProofPrimaryColor,
  resolveBodyText,
} from "./paymentProofUtils";
import {
  PaymentProofMixedText,
  registerPaymentProofPdfFonts,
} from "./paymentProofPdfText";

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 36,
    paddingHorizontal: 36,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#1f2937",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 18,
  },
  logo: {
    width: 88,
    height: 88,
    objectFit: "contain",
  },
  logoPlaceholder: {
    width: 88,
    height: 88,
  },
  companyBlock: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 12,
  },
  companyNameEn: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 4,
  },
  companyNameAr: {
    fontSize: 11,
    fontFamily: "NotoSansArabic",
    textAlign: "center",
    direction: "rtl",
  },
  dateBlock: {
    minWidth: 90,
    alignItems: "flex-end",
  },
  dateLabel: {
    fontSize: 9,
    marginBottom: 4,
  },
  dateValue: {
    fontSize: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#9ca3af",
    minWidth: 80,
    textAlign: "center",
    paddingBottom: 2,
  },
  titleBlock: {
    alignItems: "center",
    marginBottom: 14,
  },
  titleEn: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
  },
  titleAr: {
    fontSize: 16,
    fontWeight: "bold",
    fontFamily: "NotoSansArabic",
    textAlign: "center",
    direction: "rtl",
    marginTop: 2,
  },
  bodyText: {
    fontSize: 10,
    lineHeight: 1.5,
    marginBottom: 8,
    textAlign: "justify",
  },
  bodyTextArWrapper: {
    fontSize: 10,
    lineHeight: 1.5,
    marginBottom: 8,
    textAlign: "right",
    direction: "rtl",
  },
  arabicText: {
    fontFamily: "NotoSansArabic",
    direction: "rtl",
  },
  latinText: {
    fontFamily: "Helvetica",
    direction: "ltr",
  },
  table: {
    borderWidth: 1,
    borderColor: "#111827",
    marginTop: 10,
    marginBottom: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#111827",
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  labelCell: {
    width: "55%",
    borderRightWidth: 1,
    borderRightColor: "#111827",
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  valueCell: {
    width: "45%",
    paddingVertical: 6,
    paddingHorizontal: 8,
    justifyContent: "center",
  },
  labelEn: {
    fontSize: 9,
    marginBottom: 2,
  },
  labelArWrapper: {
    fontSize: 9,
    textAlign: "right",
    direction: "rtl",
  },
  valueTextWrapper: {
    fontSize: 10,
    textAlign: "right",
    direction: "rtl",
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 12,
  },
  footerBlock: {
    width: "45%",
    alignItems: "center",
  },
  footerImage: {
    width: 110,
    height: 80,
    objectFit: "contain",
    marginBottom: 6,
  },
  footerCaptionRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  footerCaptionDivider: {
    fontSize: 9,
    textAlign: "center",
    marginHorizontal: 4,
  },
  footerCaption: {
    fontSize: 9,
    textAlign: "center",
  },
  footerCaptionAr: {
    fontFamily: "NotoSansArabic",
    direction: "rtl",
  },
});

const PaymentProofPdfDocument = ({ template = {}, fieldData = {} }) => {
  registerPaymentProofPdfFonts();
  const primaryColor = normalizePaymentProofPrimaryColor(
    template.primaryColor,
    DEFAULT_PAYMENT_PROOF_PRIMARY_COLOR
  );
  const labelsEn = template.fieldLabelsEn || {};
  const labelsAr = template.fieldLabelsAr || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View>
            {template.logoUrl ? <Image src={template.logoUrl} style={styles.logo} /> : <View style={styles.logoPlaceholder} />}
          </View>
          <View style={styles.companyBlock}>
            <Text style={styles.companyNameEn}>{template.companyNameEn || ""}</Text>
            <Text style={styles.companyNameAr}>{template.companyNameAr || ""}</Text>
          </View>
          <View style={styles.dateBlock}>
            <Text style={styles.dateLabel}>{labelsEn.certificateDate || "Date"}</Text>
            <Text style={styles.dateValue}>{fieldData.certificateDate || ""}</Text>
          </View>
        </View>

        <View style={styles.titleBlock}>
          <Text style={[styles.titleEn, { color: primaryColor }]}>
            {template.titleEn || "Clearance Certificate"}
          </Text>
          <Text style={[styles.titleAr, { color: primaryColor }]}>
            {template.titleAr || "براءة ذمة"}
          </Text>
        </View>

        <Text style={styles.bodyText}>{resolveBodyText(template.bodyTextEn, fieldData, template)}</Text>
        <PaymentProofMixedText
          text={resolveBodyText(template.bodyTextAr, fieldData, template)}
          wrapperStyle={styles.bodyTextArWrapper}
          arabicStyle={styles.arabicText}
          latinStyle={styles.latinText}
        />

        <View style={styles.table}>
          {PAYMENT_PROOF_FIELD_KEYS.filter((key) => key !== "certificateDate").map((key, index, arr) => (
            <View
              key={key}
              style={[styles.tableRow, index === arr.length - 1 ? styles.tableRowLast : null]}
            >
              <View style={styles.labelCell}>
                <Text style={styles.labelEn}>{labelsEn[key] || key}</Text>
                <PaymentProofMixedText
                  text={labelsAr[key] || ""}
                  wrapperStyle={styles.labelArWrapper}
                  arabicStyle={styles.arabicText}
                  latinStyle={styles.latinText}
                />
              </View>
              <View style={styles.valueCell}>
                <PaymentProofMixedText
                  text={fieldData[key] ?? ""}
                  wrapperStyle={styles.valueTextWrapper}
                  arabicStyle={styles.arabicText}
                  latinStyle={styles.latinText}
                />
              </View>
            </View>
          ))}
        </View>

        <View style={styles.footerRow}>
          <View style={styles.footerBlock}>
            {template.stampImageUrl ? (
              <Image src={template.stampImageUrl} style={styles.footerImage} />
            ) : (
              <View style={styles.footerImage} />
            )}
            <View style={styles.footerCaptionRow}>
              <Text style={styles.footerCaption}>Stamp</Text>
              <Text style={styles.footerCaptionDivider}>/</Text>
              <Text style={[styles.footerCaption, styles.footerCaptionAr]}>الختم</Text>
            </View>
          </View>
          <View style={styles.footerBlock}>
            {template.signatureImageUrl ? (
              <Image src={template.signatureImageUrl} style={styles.footerImage} />
            ) : (
              <View style={styles.footerImage} />
            )}
            <View style={styles.footerCaptionRow}>
              <Text style={styles.footerCaption}>Signature</Text>
              <Text style={styles.footerCaptionDivider}>/</Text>
              <Text style={[styles.footerCaption, styles.footerCaptionAr]}>التوقيع</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PaymentProofPdfDocument;
